// ui/src/components/PushSync.tsx
import { useEffect } from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useAppSelector } from '../hooks/redux';

const LAST_ENDPOINT_KEY = 'sproutly.push.lastEndpoint';

// Renders nothing. On boot, if the user is authenticated AND already granted
// notification permission, reconcile the device subscription with minimal
// network:
//   - no subscription  -> re-subscribe (heals rotated/expired endpoints)
//   - endpoint changed -> subscribe() re-POSTs and re-caches
//   - endpoint same    -> no-op (zero network)
// subscribe() itself is idempotent and re-POSTs + re-caches the endpoint.
//
// The isAuthenticated gate matters because this component is mounted as a
// sibling of <App/>, so it also renders on logged-out/public screens. Without
// it, a previously-granted user who logged out (with a rotated endpoint) would
// fire POST /notifications/subscribe with no session -> 401 -> the authed
// baseQuery would needlessly dispatch logout/resetApiState. subscribe() hits an
// authed endpoint, so only reconcile when there is a session to authorize it.
const PushSync: React.FC = () => {
  const { isSupported, permission, subscribe } = usePushNotifications();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated || !isSupported || permission !== 'granted') return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        const cached = localStorage.getItem(LAST_ENDPOINT_KEY);
        const needsResync = !sub || sub.endpoint !== cached;
        if (!cancelled && needsResync) {
          await subscribe();
        }
      } catch {
        /* never block boot */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isSupported, permission, subscribe]);

  return null;
};

export default PushSync;
